import { ReactNode } from "react";
import { motion } from "framer-motion";
import logoMarca from "@/assets/logoMarca.png";

interface AuthLayoutProps {
  children: ReactNode;
  title: string;
  subtitle: string;
}

export default function AuthLayout({
  children,
  title,
  subtitle,
}: AuthLayoutProps) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden bg-gradient-to-br from-[#f8f4ee] via-[#fafaf9] to-[#f3efe8] selection:bg-accent-800/20">
      {/* Floating Orbs */}
      <motion.div
        className="absolute top-[8%] right-[12%] w-[200px] h-[200px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(195,150,74,0.12) 0%, rgba(195,150,74,0.02) 60%, transparent 80%)",
          filter: "blur(40px)",
        }}
        animate={{ x: [0, -15, 10, 0], y: [0, 20, -15, 0] }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute bottom-[12%] left-[8%] w-[280px] h-[280px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(180,140,80,0.08) 0%, rgba(180,140,80,0.01) 60%, transparent 80%)",
          filter: "blur(50px)",
        }}
        animate={{ x: [0, 20, -10, 0], y: [0, -15, 25, 0] }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute top-[40%] left-[55%] w-[150px] h-[150px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,184,74,0.07) 0%, transparent 70%)",
          filter: "blur(35px)",
        }}
        animate={{ x: [0, -20, 10, 0], y: [0, 15, -10, 0] }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Subtle Grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(195,150,74,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(195,150,74,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(ellipse 60% 50% at 50% 50%, black, transparent)",
          WebkitMaskImage:
            "radial-gradient(ellipse 60% 50% at 50% 50%, black, transparent)",
        }}
      />

      {/* Logo Area */}
      <motion.div
        className="flex flex-col items-center mb-6 relative z-10"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <img
          src={logoMarca}
          alt="Logo Engenharia do SIM"
          className="w-[42px] h-[42px] rounded-xl mb-2.5 shadow-[0_4px_12px_rgba(195,150,74,0.3)]"
        />
        <span className="font-heading text-[15px] font-semibold text-neutral-800 tracking-tight">
          Engenharia do SIM
        </span>
      </motion.div>

      {/* Card */}
      <motion.div
        className="relative w-full max-w-[420px] z-10"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.4,
          delay: 0.1,
          ease: [0.25, 0.46, 0.45, 0.94],
        }}
      >
        <div
          className="relative overflow-hidden rounded-2xl p-8 md:p-10"
          style={{
            background: "rgba(255, 255, 255, 0.75)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.6)",
            boxShadow:
              "0 8px 32px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)",
          }}
        >
          {/* Accent Line */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-accent-800 via-accent-600 to-accent-800/20" />

          {/* Header */}
          <div className="mb-7">
            <h2 className="font-display text-2xl font-bold text-neutral-900 mb-1">
              {title}
            </h2>
            <p className="text-neutral-500 text-sm">{subtitle}</p>
          </div>

          {children}
        </div>
      </motion.div>

      {/* Copyright */}
      <motion.div
        className="mt-6 text-[11px] text-neutral-400 relative z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        &copy; {new Date().getFullYear()} Engenharia do SIM. Todos os direitos
        reservados.
      </motion.div>
    </div>
  );
}
